import { useEffect } from "react";

const DEFAULT_IMAGE = "/logo512.png";

function upsertMeta(selector, attributes) {
  let element = document.head.querySelector(selector);
  if (!element) {
    element = document.createElement("meta");
    document.head.appendChild(element);
  }
  Object.entries(attributes).forEach(([key, value]) => element.setAttribute(key, value));
}

function absoluteUrl(value) {
  if (!value) return null;
  try {
    return new URL(value, window.location.origin).toString();
  } catch {
    return null;
  }
}

export function usePageMetadata({
  title,
  description,
  canonicalPath,
  image,
  robots = "index,follow",
  structuredData,
}) {
  useEffect(() => {
    const safeTitle = title || "Club Evolver | Torneos y resultados en directo";
    const safeDescription =
      description ||
      "Organiza y sigue torneos deportivos con grupos, partidos, resultados y eliminatorias en directo.";
    const canonicalUrl = absoluteUrl(canonicalPath || window.location.pathname);
    const imageUrl = absoluteUrl(image || DEFAULT_IMAGE);

    document.title = safeTitle;
    upsertMeta('meta[name="description"]', { name: "description", content: safeDescription });
    upsertMeta('meta[name="robots"]', { name: "robots", content: robots });
    upsertMeta('meta[property="og:title"]', { property: "og:title", content: safeTitle });
    upsertMeta('meta[property="og:description"]', { property: "og:description", content: safeDescription });
    upsertMeta('meta[property="og:type"]', { property: "og:type", content: "website" });
    upsertMeta('meta[property="og:site_name"]', { property: "og:site_name", content: "Club Evolver" });
    upsertMeta('meta[name="twitter:card"]', { name: "twitter:card", content: "summary_large_image" });
    upsertMeta('meta[name="twitter:title"]', { name: "twitter:title", content: safeTitle });
    upsertMeta('meta[name="twitter:description"]', { name: "twitter:description", content: safeDescription });

    if (canonicalUrl) {
      let canonical = document.head.querySelector('link[rel="canonical"]');
      if (!canonical) {
        canonical = document.createElement("link");
        canonical.setAttribute("rel", "canonical");
        document.head.appendChild(canonical);
      }
      canonical.setAttribute("href", canonicalUrl);
      upsertMeta('meta[property="og:url"]', { property: "og:url", content: canonicalUrl });
    }

    if (imageUrl) {
      upsertMeta('meta[property="og:image"]', { property: "og:image", content: imageUrl });
      upsertMeta('meta[name="twitter:image"]', { name: "twitter:image", content: imageUrl });
    }

    let jsonLd = document.head.querySelector("#page-structured-data");
    if (structuredData) {
      if (!jsonLd) {
        jsonLd = document.createElement("script");
        jsonLd.id = "page-structured-data";
        jsonLd.type = "application/ld+json";
        document.head.appendChild(jsonLd);
      }
      jsonLd.textContent = JSON.stringify(structuredData);
    } else if (jsonLd) {
      jsonLd.remove();
    }
  }, [title, description, canonicalPath, image, robots, structuredData]);
}

export function buildTournamentSeo(tournament) {
  if (!tournament) return null;

  const value = (camel, pascal) => tournament[camel] ?? tournament[pascal];
  const name = String(value("name", "Name") || "Torneo");
  const rawDescription = String(value("description", "Description") || "").trim();
  const location = String(value("location", "Location") || "").trim();
  const organizationName = String(value("organizationName", "OrganizationName") || "").trim();
  const slug = String(value("slug", "Slug") || "").trim();
  const id = String(value("id", "Id") || "").trim();
  const startDate = value("startDate", "StartDate") ?? value("day", "Day");
  const endDate = value("endDate", "EndDate");
  const isFinished = Boolean(value("isFinished", "IsFinished"));
  const visibility = value("visibility", "Visibility");
  const isIndexable = typeof visibility !== "number" || visibility === 2;
  const rawImage =
    value("imageUrl", "ImageUrl") ||
    value("organizationLogo", "OrganizationLogo") ||
    value("publicMenuLeftImageUrl", "PublicMenuLeftImageUrl");
  const image = typeof rawImage === "string" && /^https?:\/\//i.test(rawImage) ? rawImage : null;
  const fallbackDescription = `Consulta grupos, partidos, resultados y eliminatorias de ${name}${location ? ` en ${location}` : ""}.`;
  const description = (rawDescription || fallbackDescription).slice(0, 160);
  const canonicalPath = `/tournamentactions/${encodeURIComponent(slug || id)}`;
  const url = absoluteUrl(canonicalPath);

  return {
    title: `${name} | Resultados y partidos | Club Evolver`,
    description,
    canonicalPath,
    image,
    robots: isIndexable ? "index,follow" : "noindex,nofollow",
    structuredData: {
      "@context": "https://schema.org",
      "@type": "SportsEvent",
      name,
      description,
      url,
      startDate: startDate ? new Date(startDate).toISOString() : undefined,
      endDate: endDate ? new Date(endDate).toISOString() : undefined,
      eventStatus: isFinished
        ? "https://schema.org/EventCompleted"
        : "https://schema.org/EventScheduled",
      eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
      image: image ? [image] : undefined,
      location: location
        ? {
            "@type": "Place",
            name: location,
          }
        : undefined,
      organizer: organizationName
        ? {
            "@type": "Organization",
            name: organizationName,
          }
        : undefined,
    },
  };
}
